
import { useCallback } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { removeImage } from '@/utils/imageUtils';

export const useSweepstakesDelete = (fetchSweepstakes: () => Promise<void>) => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm(t('admin.confirmDeleteSweepstakes'))) return;
    
    try {
      // Get the sweepstake details first to get the image URL
      const { data: sweepstakeData, error: getError } = await supabase
        .from('sweepstakes')
        .select('*')
        .eq('id', id)
        .single();
        
      if (getError) throw getError;
      
      // If the sweepstakes has an image, remove it from storage
      if (sweepstakeData && 
          'image_url' in sweepstakeData && 
          sweepstakeData.image_url && 
          typeof sweepstakeData.image_url === 'string' && 
          sweepstakeData.image_url.includes('events/')) {
        try {
          await removeImage(sweepstakeData.image_url, 'events');
        } catch (error) {
          console.error('Error removing image:', error);
          // Don't block the deletion if image removal fails
        }
      }
      
      const { error } = await supabase
        .from('sweepstakes')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast({
        title: t('admin.success'),
        description: t('admin.sweepstakesDeleted'),
      });
      
      fetchSweepstakes();
    } catch (error) {
      console.error('Error deleting sweepstakes:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorDeletingSweepstakes'),
        variant: 'destructive',
      });
    }
  }, [t, toast, fetchSweepstakes]);
  
  return { handleDelete };
};
